import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { DataEditContextValidation } from 'Components/reusable/DataContext/Validation'
import Grid from '@mui/material/Grid2'
import DeleteEmployeeConfirm from 'Components/General/Admin/AdminEmployees/DeleteEmployeeConfirm'
import DeleteOrganizationConfirm from 'Components/General/Admin/AdminOrganizations/DeleteOrganizationConfirm'
import DeleteSitesConfirm from 'Components/General/Admin/AdminOrganizations/DeleteSitesConfirm'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import DeleteConnectedSkillConfirm from 'Components/General/SkillsCard/DeleteConnectedSkillConfirm'
import DeleteConfirm from 'Components/reusable/Confirmations/DeleteConfirm'
import DataContext, { NewOrExisting } from 'Components/reusable/DataContext'
import LoadingIndicator from 'Components/reusable/LoadingIndicator'
import { useNotificationPopup } from 'Components/reusable/Notification'
import { cloneDeep, isEqual } from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Flag from 'react-world-flags'
import { IAllocation } from 'types/assignmentInterfaces'
import {
  IPersonCertificate,
  ICourse,
  IEducation,
  IEmployer,
  ILanguageSkill,
  IPersonSkillOrIndustryOrRole,
  IProject,
  IReference,
  IPersonCompetence,
} from 'types/cvsInterfaces'
import { IError } from 'types/error'
import { IAbout } from 'types/layoutInterfaces'
import { ITeam } from 'types/teamInterfaces'
import { INetworkRegister } from 'types/networkInterfaces'
import { COUNTRY_CODES } from 'constants/caleo-constants'
import CardContentText from '../CaleoCustomComponents/CardContentText'
import CloseButton from '../IconButtons/CloseButton'
import YellowOutlinedButton from '../Buttons/YellowOutlinedButton'
import { IChatRoom } from 'types/chatInterfaces'
import colors from 'constants/colors'
import { chooseDBTranslation } from 'utils/translations'
import { ObjectSchema } from 'yup'
import { adminAPI } from 'api/AdminAPI'
import { IMaintenance } from 'types/maintenanceInterfaces'
import { IModalDraft } from 'types/hocTypes'

type T =
  | IReference
  | IPersonSkillOrIndustryOrRole
  | ILanguageSkill
  | IProject
  | IEmployer
  | IEducation
  | IPersonCertificate
  | IPersonCompetence
  | ICourse
  | IAbout
  | IAllocation
  | ITeam
  | INetworkRegister
  | IChatRoom
  | IMaintenance

type ModalApi = {
  create: (cvId: number, item: unknown) => Promise<unknown>
  save?: unknown
  networkSave?: unknown
  delete: (id: number, cvId?: number) => Promise<void>
}

type ModalItem = {
  id?: number
  name?: string
}

type EditModalProps = {
  item?: ModalItem | null
  initialData?: Promise<NewOrExisting<T>> | NewOrExisting<T>
  api?: ModalApi
  schema?: ObjectSchema
  localeBase?: string
  customButtons?: boolean
  submitOnModal?: boolean
  deleteOnModal?: boolean
  addSelected?: boolean
  checkFields?: string[]
  cvId?: number
  onClose: (value?: ModalItem | { newItem?: unknown } | Record<string, unknown>) => void
  onAdvancedClose?: (type: 'create' | 'edit' | 'delete', item: unknown) => void
  networkManage?: boolean
  noEscClose?: boolean
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  fullWidth?: boolean
  hideLanguageHelperText?: boolean
  extraButtonAction?: (item: ModalItem) => void
  extraButtonLabel?: string
  duplicateFunction?: (item: ModalItem) => void
  disableDelete?: boolean
  disableSubmit?: boolean
  submitColor?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  employeeModal?: boolean
  skillsModal?: boolean
  organizationModal?: boolean
  siteModal?: boolean
  releaseNoteModal?: boolean
  roomModal?: boolean
  releseNoteModal?: boolean
  deleteNote?: (item: ModalItem) => void
  type?: string
  /**
   * Keeps unsaved form data as a draft (opt-in, used by the CV wizard). The dialog starts from the draft if there
   * is one, saves the form once the user has touched it, and clears the draft whenever it closes (save, delete,
   * cancel). Leaving the page without closing the dialog keeps the draft.
   */
  draft?: IModalDraft
  [key: string]: unknown
}

/**
 * Edit modal higher order component.
 *
 * @param ModalComponent - The modal component to wrap.
 * @returns The wrapped modal component.
 * @notExported
 */
const newWithEditModal = <TModalProps extends object>(ModalComponent: React.ComponentType<TModalProps>) => {
  const EditModal = (props: EditModalProps) => {
    const { t, i18n } = useTranslation()
    const { setSuccessNotificationPopup, setNotificationPopup } = useNotificationPopup()
    const [data, setData] = useState<NewOrExisting<T> | null>(null)
    const theme = useTheme()
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
    const [confirmation, setConfirmation] = useState<boolean>(false)
    const [backendError, setBackendError] = useState<IError>()
    const [multipleItems, setMultipleItems] = useState<NewOrExisting<T>[]>([])
    const [schema, setSchema] = useState<ObjectSchema>()
    const [api, setApi] = useState<ModalApi>()
    const [localeBase, setLocaleBase] = useState<string>()
    const [submitLocally, setSubmitLocally] = useState<boolean>(false)
    const [deleteLocally, setDeleteLocally] = useState<boolean>(false)
    const [deleted, setDeleted] = useState<boolean>(false)
    const [buttonLoading, setButtonLoading] = useState<boolean>(false)
    const [extraActions, setExtraActions] = useState<React.ReactNode>()
    const [extraData, setExtraData] = useState(null)

    const initializedModalRef = useRef<string | undefined>(undefined)
    /** Form data to compare the draft against; set when the user first touches the dialog (see `markTouched`). */
    const draftBaselineRef = useRef<NewOrExisting<T> | null>(null)
    const [restoredFromDraft, setRestoredFromDraft] = useState<boolean>(false)

    const saveLabel = props.customButtons ? t(`${localeBase}.save`) : t('save')
    const updateLabel = props.customButtons ? t(`${localeBase}.update`) : t('update')

    useEffect(() => {
      const modalIdentity = props.item ? `item:${props.item.id ?? 'new'}` : `cv:${props.cvId ?? ''}`
      if (initializedModalRef.current === modalIdentity) return

      initializedModalRef.current = modalIdentity
      ;(async () => {
        const savedData = props.item ? cloneDeep(props.item) : props.initialData ? await props.initialData : null
        const draftData = (props.draft?.load() as NewOrExisting<T> | null | undefined) ?? null
        const initialData = draftData ?? savedData
        if (!initialData) {
          return
        }
        if (draftData) {
          // Compare later edits with the saved values, so undoing them all removes the draft.
          draftBaselineRef.current = savedData
          setRestoredFromDraft(true)
        }
        setData(initialData)
        setSchema(props.schema)
        setApi(props.api)
        setLocaleBase(props.localeBase)
      })()
    }, [props.api, props.cvId, props.draft, props.initialData, props.item, props.localeBase, props.schema])

    const { draft } = props

    useEffect(() => {
      if (!draft || !data || !draftBaselineRef.current) return
      if (isEqual(data, draftBaselineRef.current)) {
        draft.clear()
      } else {
        draft.save(data)
      }
    }, [data, draft])

    /**
     * Starts draft tracking on the first key or pointer press inside the dialog. Taking the baseline then, instead of
     * when the dialog opens, keeps changes the form makes by itself while mounting (e.g. default values) out of the draft.
     */
    const markTouched = () => {
      if (draft && !draftBaselineRef.current && data) {
        draftBaselineRef.current = cloneDeep(data)
      }
    }

    /**
     * Closes the dialog; a closed dialog has nothing unsaved to keep, so its draft is removed. Passes on every
     * argument, since some dialogs close with more than one (RoleModal sends the edited role and the original row).
     */
    const closeModal = (...args: Parameters<typeof props.onClose>) => {
      draft?.clear()
      props.onClose(...args)
    }

    const sendData = (type: 'create' | 'edit' | 'delete', newItem: unknown) => {
      if (props.onAdvancedClose) props.onAdvancedClose(type, newItem)
    }

    const getDeleteNotificationMessage = () => {
      if (props.employeeModal) {
        return t('admin.employee.deleted')
      }

      const deleteLocaleKey = localeBase ? `${localeBase}.deleted` : undefined
      if (deleteLocaleKey && i18n.exists(deleteLocaleKey)) {
        return t(deleteLocaleKey)
      }

      if (i18n.exists('deleteSuccess')) {
        return t('deleteSuccess')
      }

      return t('changesSaved')
    }

    const onSubmit = async () => {
      if (buttonLoading) return

      setButtonLoading(true)

      if (props.submitOnModal === undefined) {
        if (!api) {
          setButtonLoading(false)
          return
        }

        let newItem: unknown
        if (multipleItems && multipleItems.length > 0) {
          try {
            const items = multipleItems

            if (props.addSelected && props.checkFields && props.checkFields.length && data) {
              const checked = props.checkFields.every((field: string) => {
                const key = field as keyof NewOrExisting<T>
                return data[key] !== null && data[key] !== undefined && data[key] !== ''
              })
              if (checked) {
                items.push(data)
              }
            }

            const newItems: unknown[] = []

            for (const item of items) {
              newItems.push(await api.create(props.cvId ?? 0, item))
            }

            closeModal({ newItem: newItems })
            setSuccessNotificationPopup()
          } catch (err) {
            setBackendError(err as IError)
          }
        } else {
          try {
            if (props.item && props.item.id) {
              if (props.networkManage && typeof api.networkSave === 'function') {
                newItem = await (api.networkSave as (item: unknown, data?: unknown) => Promise<unknown>)(
                  props.item,
                  data
                )
                sendData('edit', newItem)
              } else if (typeof api.save === 'function') {
                newItem = await (api.save as (item: unknown, data?: unknown) => Promise<unknown>)(props.item, data)
                sendData('edit', newItem)
              }
            } else {
              newItem = await api.create(props.cvId ?? 0, data)
              sendData('create', newItem)
            }
            if (newItem) {
              closeModal({ newItem })
              setSuccessNotificationPopup()
            }
          } catch (err) {
            setBackendError(err as IError)
          }
        }
        setButtonLoading(false)
      } else {
        setSubmitLocally(true)
      }
    }

    const deleteItem = async () => {
      const itemId = typeof props.item?.id === 'number' ? props.item.id : undefined
      if (props.deleteOnModal === undefined) {
        try {
          if (props.item && itemId !== undefined && api) {
            sendData('delete', props.item)
            await api.delete(itemId, props.cvId)
            {
              setNotificationPopup({
                message: getDeleteNotificationMessage(),
                type: 'success',
                duration: 'short',
              })
              setDeleted(true)
              closeModal({ newItem: { deleted: itemId, item: props.item, cvId: props.cvId ?? undefined } })
            }
          }
        } catch (error) {
          setBackendError(error as IError)
        }
      } else {
        setDeleteLocally(true)
        setConfirmation(false)
      }
    }

    const add = () => {
      if (!data) return
      if (!props.initialData) return

      setMultipleItems([...multipleItems, data])
      setData(props.initialData)
    }

    const delayedEmployeeDelete = async (deleteDate: Date | null) => {
      const itemId = typeof props.item?.id === 'number' ? props.item.id : undefined
      if (props.item && itemId !== undefined && deleteDate) {
        try {
          await adminAPI.setUserForDelete(itemId, deleteDate)
          {
            setNotificationPopup({
              message: t('admin.employee.setToBeDeleted'),
              type: 'success',
            })
            setExtraData(null)
            closeModal({})
          }
        } catch (error) {
          setBackendError(error as IError)
        }
      }
    }

    if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
      return <ErrorOverlay error={backendError} setOpen={setBackendError} />
    }

    if (!data) {
      return <LoadingIndicator />
    }

    return (
      <DataContext
        data={data}
        onChange={setData}
        onSubmit={onSubmit}
        schema={schema ?? props.schema}
        localeBase={localeBase}
      >
        <Dialog
          open
          fullScreen={fullScreen}
          aria-labelledby="form-dialog-title"
          onKeyDownCapture={markTouched}
          onPointerDownCapture={markTouched}
          onClose={() => {
            if (props.noEscClose) {
              return
            }
            closeModal({})
          }}
          maxWidth={props.maxWidth}
          fullWidth={props.fullWidth ? true : false}
          sx={{
            '.MuiDialog-paper': {
              backgroundColor: colors.secondaryBackground,
              borderRadius: 1,
            },
          }}
        >
          <DialogTitle sx={{ backgroundColor: colors.secondaryBackground }}>
            {props.item ? (
              <span id="form-dialog-title">{t(`${localeBase}.editTitle`)}</span>
            ) : (
              <span id="form-dialog-title">{t(`${localeBase}.addTitle`)}</span>
            )}
            {!!props.onClose && <CloseButton testId="button-new-edit-modal-close" clickAction={() => closeModal({})} />}
          </DialogTitle>
          {React.createElement(ModalComponent, {
            ...props,
            onClose: closeModal,
            restoredFromDraft,
            multipleItems,
            addMultiple: () => add(),
            removeFromArray: (items: NewOrExisting<T>[]) => setMultipleItems(items),
            data,
            setData,
            setSchema,
            setLocaleBase,
            setApi,
            submitIndicator: submitLocally,
            deleteIndicator: deleteLocally,
            resetSubmit: () => {
              setButtonLoading(false)
              setSubmitLocally(false)
            },
            deleted,
            setExtraActions,
          } as unknown as React.Attributes & TModalProps)}
          <DialogActions sx={{ p: 2 }}>
            <Grid
              container
              justifyContent={props.maxWidth === 'xs' ? 'flex-end' : 'space-between'}
              direction="row"
              alignItems="center"
              style={{ display: 'flex' }}
              spacing={1}
            >
              <Grid>
                {props.hideLanguageHelperText ? null : (
                  <Grid container spacing={2}>
                    <Grid>
                      <Flag
                        style={{ height: '20px', width: '32px' }}
                        code={COUNTRY_CODES[i18n.language as keyof typeof COUNTRY_CODES] ?? COUNTRY_CODES.en}
                        fallback={<span>??</span>}
                      />
                    </Grid>
                    <Grid>
                      <CardContentText type="description">{t('translationImport.helperText')}</CardContentText>
                    </Grid>
                  </Grid>
                )}
              </Grid>
              <Grid>
                <Grid container spacing={1}>
                  {extraActions && <Grid>{extraActions}</Grid>}
                  {props.extraButtonAction && props.item && props.extraButtonLabel && (
                    <Grid>
                      <YellowOutlinedButton
                        clickAction={() => {
                          if (props.item) {
                            props.extraButtonAction?.(props.item)
                          }
                        }}
                        valid
                        label={props.extraButtonLabel}
                        noMargin
                      />
                    </Grid>
                  )}
                  {props.duplicateFunction && props.item && typeof props.item.id === 'number' && (
                    <Grid>
                      <YellowOutlinedButton
                        clickAction={() => {
                          if (props.item) {
                            props.duplicateFunction?.(props.item)
                          }
                        }}
                        valid
                        label={t('duplicate')}
                        noMargin
                      />
                    </Grid>
                  )}
                  {props.item && props.disableDelete !== true && (
                    <Grid>
                      <Button
                        data-testid="button-edit-modal-delete"
                        variant="outlined"
                        onClick={() => setConfirmation(true)}
                      >
                        {t('delete')}
                      </Button>
                    </Grid>
                  )}
                  {props.disableSubmit !== true && (
                    <Grid>
                      <DataEditContextValidation>
                        {({ valid }) => (
                          <Button
                            data-testid={props.item ? 'button-edit-modal-update' : 'button-edit-modal-save'}
                            disabled={multipleItems.length > 0 ? multipleItems.length < 1 && !valid : !valid}
                            onClick={onSubmit}
                            color={props.submitColor}
                            variant="contained"
                          >
                            {buttonLoading ? <LoadingIndicator /> : <>{props.item ? updateLabel : saveLabel}</>}
                          </Button>
                        )}
                      </DataEditContextValidation>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </DialogActions>
          {!props.employeeModal &&
            !props.skillsModal &&
            !props.organizationModal &&
            !props.siteModal &&
            !props.releaseNoteModal &&
            !props.roomModal &&
            props.item && (
              <DeleteConfirm open={confirmation} confirm={deleteItem} onClose={() => setConfirmation(false)} />
            )}
          {props.siteModal && props.item && (
            <DeleteSitesConfirm
              item={props.item}
              open={confirmation}
              confirm={deleteItem}
              onClose={() => setConfirmation(false)}
            />
          )}
          {props.skillsModal && props.item && (
            <DeleteConnectedSkillConfirm
              item={props.item}
              open={confirmation}
              confirm={deleteItem}
              type={props.type}
              onClose={() => setConfirmation(false)}
            />
          )}
          {props.organizationModal && props.item && (
            <DeleteOrganizationConfirm
              item={props.item}
              open={confirmation}
              confirm={deleteItem}
              onClose={() => setConfirmation(false)}
              maxWidth="md"
              fullWidth={true}
              networkManage={props.networkManage}
            />
          )}
          {props.employeeModal && !props.networkManage && props.item && typeof props.item.id === 'number' && (
            <DeleteEmployeeConfirm
              open={confirmation}
              confirm={deleteItem}
              onClose={() => setConfirmation(false)}
              item={props.item}
              setDeleteDate={setExtraData}
              data={extraData}
              extraButtonAction={() => delayedEmployeeDelete(extraData)}
              extraButtonLabel={t('admin.employee.deletebyDate')}
            />
          )}
          {props.employeeModal && props.networkManage && props.item && typeof props.item.id === 'number' && (
            <DeleteEmployeeConfirm
              open={confirmation}
              confirm={deleteItem}
              onClose={() => setConfirmation(false)}
              item={props.item}
              networkManage={props.networkManage}
              setDeleteDate={setExtraData}
              data={extraData}
              extraButtonAction={() => delayedEmployeeDelete(extraData)}
              extraButtonLabel={t('admin.employee.deletebyDate')}
            />
          )}
          {props.releseNoteModal && props.item && props.deleteNote && (
            <DeleteConfirm
              open={confirmation}
              confirmationText={t('admin.releaseNotes.delete.confirmationText', {
                name:
                  (chooseDBTranslation(i18n, props.item as never) as unknown as { title?: string } | null)?.title ?? '',
              })}
              confirm={() => {
                if (props.item) {
                  props.deleteNote?.(props.item)
                }
                setConfirmation(false)
                closeModal()
              }}
              onClose={() => setConfirmation(false)}
            />
          )}
          {props.roomModal && props.item && (
            <DeleteConfirm
              open={confirmation}
              confirmationText={t('chat.room.deleteText', {
                name: typeof props.item.name === 'string' ? props.item.name : '',
              })}
              confirm={() => {
                setDeleteLocally(true)
                setConfirmation(false)
              }}
              onClose={() => setConfirmation(false)}
            />
          )}
        </Dialog>
      </DataContext>
    )
  }
  return EditModal
}

export default newWithEditModal
